import React, { useEffect, useState, useCallback } from 'react';
import { vendorAPI } from '../api/client';
import Layout from '../components/Layout';
import { useToast } from '../components/ToastProvider';
import RecordPreviewModal from '../components/RecordPreviewModal';

function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewVendor, setPreviewVendor] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    address: '', 
    city: '', 
    state: '', 
    postal_code: '', 
    country: '', 
    tax_id: '', 
    payment_terms: '',
    account_number: ''
  });
  const { showToast } = useToast();

  const loadVendors = useCallback(async () => {
    try {
      const response = await vendorAPI.getAll();
      setVendors(response.data.data || response.data || []);
    } catch (err) {
      console.error('Failed to load vendors', err);
      showToast({
        title: 'Error',
        message: 'Failed to load vendors',
        tone: 'error'
      });
    }
  }, [showToast]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await vendorAPI.update(editingId, formData);
        showToast({
          title: 'Success',
          message: 'Vendor updated successfully',
          tone: 'success'
        });
      } else {
        await vendorAPI.create(formData);
        showToast({
          title: 'Success',
          message: 'Vendor created successfully',
          tone: 'success'
        });
      }
      setFormData({ 
        name: '', 
        email: '', 
        phone: '', 
        address: '', 
        city: '', 
        state: '', 
        postal_code: '', 
        country: '', 
        tax_id: '', 
        payment_terms: '',
        account_number: ''
      });
      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      loadVendors();
    } catch (err) {
      console.error('Failed to save vendor', err);
      showToast({
        title: 'Error',
        message: 'Failed to save vendor',
        tone: 'error'
      });
    }
  };

  const handleEdit = (vendor) => {
    setFormData({
      name: vendor.name,
      email: vendor.email,
      phone: vendor.phone,
      address: vendor.address,
      city: vendor.city,
      state: vendor.state,
      postal_code: vendor.postal_code,
      country: vendor.country,
      tax_id: vendor.tax_id,
      payment_terms: vendor.payment_terms,
      account_number: vendor.account_number,
    });
    setEditingId(vendor.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData({ 
      name: '', 
      email: '', 
      phone: '', 
      address: '', 
      city: '', 
      state: '', 
      postal_code: '', 
      country: '', 
      tax_id: '', 
      payment_terms: '',
      account_number: ''
    });
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this vendor? This will not delete associated expenses.')) {
      try {
        await vendorAPI.delete(id);
        loadVendors();
        showToast({
          title: 'Success',
          message: 'Vendor deleted successfully',
          tone: 'success'
        });
      } catch (err) {
        console.error('Failed to delete vendor', err);
        showToast({
          title: 'Error',
          message: 'Failed to delete vendor',
          tone: 'error'
        });
      }
    }
  };

  return (
    <Layout pageTitle="Vendors" pageSubtitle="Manage your supplier and vendor information">
      <div className="flex justify-end mb-8">
        <button 
          onClick={() => showForm ? handleCancel() : setShowForm(true)} 
          className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white font-medium rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          {showForm ? 'Cancel' : '+ New Vendor'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-6">{isEditing ? 'Edit Vendor' : 'Add New Vendor'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tax ID</label>
                <input
                  type="text"
                  value={formData.tax_id}
                  onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                <input
                  type="text"
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Terms</label>
                <input
                  type="text"
                  value={formData.payment_terms}
                  onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                  placeholder="e.g., Net 30, Due on receipt"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Postal Code</label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                />
              </div>
              <button type="submit" className="md:col-span-2 px-6 py-2 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition-all duration-200">
                {isEditing ? 'Update Vendor' : 'Create Vendor'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Phone</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">City</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Tax ID</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Payment Terms</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {vendors.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-sm text-gray-500">
                    No vendors yet. Create your first vendor to get started.
                  </td>
                </tr>
              ) : (
                vendors.map((vendor) => (
                  <tr
                    key={vendor.id}
                    className="hover:bg-gray-50 transition cursor-pointer"
                    onClick={() => setPreviewVendor(vendor)}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{vendor.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{vendor.email || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{vendor.phone || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{vendor.city || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{vendor.tax_id || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{vendor.payment_terms || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(vendor);
                          }} 
                          className="text-blue-500 hover:text-blue-700 font-medium text-sm transition"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(vendor.id);
                          }} 
                          className="text-red-500 hover:text-red-700 font-medium text-sm transition"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {previewVendor && (
        <RecordPreviewModal
          title={previewVendor.name || `Vendor #${previewVendor.id}`}
          subtitle="Vendor Record"
          onClose={() => setPreviewVendor(null)}
          fields={[
            { label: 'Email', value: previewVendor.email },
            { label: 'Phone', value: previewVendor.phone },
            { label: 'Address', value: previewVendor.address },
            { label: 'City', value: previewVendor.city },
            { label: 'State', value: previewVendor.state },
            { label: 'Postal Code', value: previewVendor.postal_code },
            { label: 'Country', value: previewVendor.country },
            { label: 'Tax ID', value: previewVendor.tax_id },
            { label: 'Account Number', value: previewVendor.account_number },
            { label: 'Payment Terms', value: previewVendor.payment_terms },
          ]}
        />
      )}
    </Layout>
  );
}

export default Vendors;
